import React, { FC, useCallback, useEffect, useMemo } from 'react';
import { nullSafeGet, valueCompareWithoutType } from '../../utils/DataAccessUtils';
import { Form } from '@ant-design/compatible';
import OWAsyncRadio from '../visual_radio_input/VisualRadioInput';
import { connect } from 'react-redux';
import { workOrderPrioritiesWithoutPMRestCrudThunksForBuyer } from '../../thunks/work_order_priorities_thunks';
import { Button, message } from 'antd';
import { WO_STEP_NAMES } from '../../utils/DataConstants';
import { getRecordsForCompositeTargetCollection } from '../../reducers/standard_reducer_utils';

const TC_NAME = 'WORK_ORDER_FORM_WORK_ORDER_PRIORITIES_AUTOCOMPLETE';

const WOPriority: FC<any> = ({
	goNextAfterBriefPause,
	setState,
	form,
	state,
	goBack,
	goNext,
	isProcessing,
	workOrderPriorities,
	fetchMultipleWorkOrderPriorities,
	fetchWorkOrderPriorities,
	fetchWorkOrderPriority,
}): React.ReactElement => {
	const { getFieldDecorator } = form;

	const onNextStep = useCallback(() => {
		nullSafeGet('woPriority.id', state)
			? goNextAfterBriefPause([], WO_STEP_NAMES.SUPPLIER, WO_STEP_NAMES.PRIORITY)
			: message.error('Work order priority cannot be empty!');
	}, [goNextAfterBriefPause, state]);

	const getPriorityObject = useCallback(
		(woPriorityId) => {
			const priorities = getRecordsForCompositeTargetCollection(workOrderPriorities, TC_NAME);

			const priority = priorities.find(({ id }) => valueCompareWithoutType(id, woPriorityId));
			if (priority) {
				return Promise.resolve(priority);
			}

			return fetchWorkOrderPriority(woPriorityId);
		},
		[fetchWorkOrderPriority, workOrderPriorities]
	);

	const handleWorkOrderPriority = useCallback(
		(woPriorityId) => {
			getPriorityObject(woPriorityId).then((woPriority) => {
				setState({ woPriority }, () => {
					goNextAfterBriefPause([], WO_STEP_NAMES.SUPPLIER, WO_STEP_NAMES.PRIORITY);
				});
			});
		},
		[getPriorityObject, goNextAfterBriefPause, setState]
	);

	const handleWorkOrderPriorityClick = useCallback(
		(woPriorityId) => {
			getPriorityObject(woPriorityId).then((woPriority) => {
				setState({ woPriority }, () => {
					goNext([], WO_STEP_NAMES.SUPPLIER, WO_STEP_NAMES.PRIORITY);
				});
			});
		},
		[getPriorityObject, goNext, setState]
	);

	return (
		<div key={11} className="newWorkOrderWizardPage__section">
			<h3 className="newWorkOrderWizardPage__title">How quickly does this need to get fixed?</h3>
			<Form.Item
				style={{
					marginBottom: 20,
					width: '100%',
				}}
			>
				{getFieldDecorator(`woPriorityId`, {
					initialValue: nullSafeGet('workOrderPriorityId', state),
				})(
					<OWAsyncRadio
						mode="default"
						fieldName="woPriorityId"
						stateSlice={workOrderPriorities}
						onChange={handleWorkOrderPriority}
						onClick={handleWorkOrderPriorityClick}
						targetCollectionName={TC_NAME}
						fetchMultiple={(ids, targetCollectionName) =>
							fetchMultipleWorkOrderPriorities(ids, targetCollectionName)
						}
						fetchData={(
							searchText,
							targetCollectionName,
							pagination = null,
							sorting = {
								sort_by: 'expectedResolutionInSeconds',
								order: 'descend',
							},
							filters = null,
							addToTargetCollection = false
						) =>
							fetchWorkOrderPriorities(
								{ name: searchText },
								targetCollectionName,
								pagination,
								sorting,
								filters,
								addToTargetCollection
							)
						}
						renderRecord={(workOrderPriority) => workOrderPriority.name}
						keepFetchedOrder={true}
					/>
				)}
			</Form.Item>
			<div className="flex flex-row">
				<Button
					size="large"
					type="ghost"
					onClick={goBack}
					loading={isProcessing}
					disabled={isProcessing}
				>
					Back
				</Button>
				<Button
					size="large"
					type="primary"
					className="ml-4"
					onClick={onNextStep}
					loading={isProcessing}
					disabled={isProcessing}
				>
					Next step
				</Button>
			</div>
		</div>
	);
};

const mapStateToProps = (state) => ({
	companyConfig: state.company_config.detail,
	workOrderPriorities: state.work_order_priorities,
	currentUser: state.session.currentUser,
});

const mapDispatchToProps = (dispatch) => ({
	fetchWorkOrderPriorities: (
		params,
		targetCollectionName,
		pagination,
		sorting,
		filters,
		addToTargetCollection
	) =>
		dispatch(
			workOrderPrioritiesWithoutPMRestCrudThunksForBuyer.read(
				params,
				targetCollectionName,
				pagination,
				sorting,
				filters,
				addToTargetCollection
			)
		),
	fetchMultipleWorkOrderPriorities: (ids, targetCollectionName) =>
		dispatch(
			workOrderPrioritiesWithoutPMRestCrudThunksForBuyer.readMultiple(ids, targetCollectionName)
		),
	fetchWorkOrderPriority: (id) =>
		dispatch(workOrderPrioritiesWithoutPMRestCrudThunksForBuyer.readOne(id)),
});

export default connect(mapStateToProps, mapDispatchToProps)(WOPriority);
